// Who stands inside an aura, from the Roll20 map. The DM's rule: a creature
// is inside when the centre of ANY square it occupies is within the radius —
// a Huge creature counts if one of its nine squares' centres reaches, and
// not otherwise. The radius is measured the way Roll20 draws auras: from the
// caster token's edge, so the circle is (radius + half the token) wide.
import { newRequestId, request } from "../bridge";
import type { Character, Layout } from "./types";
import { characterForToken, type Roll20BarsLink } from "./roll20Bars";
import type { BarNo } from "./tokenBars";
import type { RawBars } from "./barRemap";
import { normName as norm } from "./text";
import type { Roll20TokensResult } from "./laneResults";

export interface MapToken { id: string; name: string; layer: string; represents: string; markers?: string[]; left: number; top: number; width: number; height: number; bar1?: string; bar2?: string; bar3?: string; bar4?: string; bars?: RawBars; vision?: { sight: boolean; night: number; effect: string }; sheet?: { npc: boolean; senses: string; name: string } | null }

export const barValue = (t: MapToken, n: BarNo) => String((n === 1 ? t.bar1 : n === 2 ? t.bar2 : n === 3 ? t.bar3 : t.bar4) ?? "");
/* A bar's value as a NUMBER, or null when it holds none.

   One place, because the parse was written inline at a call site on 2026-09-29
   with its backslash eaten — /^-?d+/ instead of /^-?\d+/ — so it matched a
   literal "d", every bar read as empty, and the DM was told "its Bar 3 holds no
   number" about a token whose Bar 3 plainly held 99. A wrong error message about
   the DM's own data is worse than a crash: it sends them to look at Roll20.

   Leading text is allowed ("99/120" is a bar the DM typed) and a negative is kept:
   a creature at -3 is a creature someone has been tracking past zero. */
export const barNumber = (t: MapToken, n: BarNo): number | null => {
  const m = barValue(t, n).trim().match(/^-?\d+/);
  return m ? Number(m[0]) : null;
};
export interface PageGeometry { gridType: string; snapping: number; scaleNumber: number; scaleUnits: string; unitPx: number }
export interface MapRead { ok: boolean; error?: string; pageName?: string; page: PageGeometry | null; tokens: MapToken[]; barCount?: 3 | 4 }

export async function readMap(link: Roll20BarsLink): Promise<MapRead> {
  const r = await request<Roll20TokensResult>({ type: "DM_SCREEN_ROLL20_TOKENS_REQUEST", requestId: newRequestId("roll20-map"), tabId: link.tabId, frameId: link.frameId, scope: "page" }, { resultType: "DM_SCREEN_ROLL20_TOKENS_RESULT", timeoutMs: 15000 });
  if (!r?.ok) return { ok: false, error: r?.error || "Roll20 did not answer", page: null, tokens: [] };
  const tokens: MapToken[] = (r.tokens || []).filter(Boolean).map((t: any) => ({ id: String(t.id), name: String(t.name || ""), layer: String(t.layer || ""), represents: String(t.represents || ""), markers: Array.isArray(t.markers) ? t.markers.map(String) : [], left: Number(t.left) || 0, top: Number(t.top) || 0, width: Number(t.width) || 0, height: Number(t.height) || 0, bar1: String(t.bar1?.value ?? ""), bar2: String(t.bar2?.value ?? ""), bar3: String(t.bar3?.value ?? ""), bar4: t.bar4 ? String(t.bar4.value ?? "") : undefined, bars: Object.fromEntries([1, 2, 3, 4].filter((n) => t[`bar${n}`]).map((n) => [`bar${n}`, { value: String(t[`bar${n}`].value ?? ""), max: String(t[`bar${n}`].max ?? ""), link: String(t[`bar${n}`].link ?? "") }])) as RawBars, vision: t.vision, sheet: t.sheet ?? null }));
  const barCount = (r.bars === 4 || tokens.some((t) => t.bar4 !== undefined)) ? 4 : 3;
  // The lane carries the page geometry as unknown — PageGeometry is declared
  // here, and laneResults.ts cannot import it without a cycle. One cast, at the
  // boundary where the shape is known, beats a second copy of those fields.
  return { ok: true, pageName: r.pageName, page: (r.page as PageGeometry | undefined) || null, tokens, barCount };
}

/** Pixels per grid square and feet per square on this page. */
export function cellOf(page: PageGeometry | null): { px: number; feet: number } {
  const px = page ? Math.max(1, (page.unitPx || 70) * (page.snapping || 1)) : 70;
  const feet = page ? Math.max(1, page.scaleNumber || 5) : 5;
  return { px, feet };
}

/** Centres of every square a token occupies (a 1×1 token is its own centre;
 * a token smaller than a square still counts as one square). */
export function squareCentres(t: MapToken, cellPx: number): { x: number; y: number }[] {
  const cols = Math.max(1, Math.round(t.width / cellPx));
  const rows = Math.max(1, Math.round(t.height / cellPx));
  if (cols === 1 && rows === 1) return [{ x: t.left, y: t.top }];
  const x0 = t.left - (cols * cellPx) / 2, y0 = t.top - (rows * cellPx) / 2;
  const out: { x: number; y: number }[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) out.push({ x: x0 + (c + 0.5) * cellPx, y: y0 + (r + 0.5) * cellPx });
  return out;
}

/** Tokens inside `radiusFt` of the caster token (the caster itself excluded). */
export function tokensInside(caster: MapToken, radiusFt: number, tokens: MapToken[], page: PageGeometry | null): MapToken[] {
  const cell = cellOf(page);
  const radiusPx = (radiusFt / cell.feet) * cell.px + Math.max(caster.width, caster.height) / 2;
  const limit = radiusPx + 0.5; // half a pixel of grace for snapped positions
  return tokens.filter((t) => t.id !== caster.id && squareCentres(t, cell.px).some((p) => Math.hypot(p.x - caster.left, p.y - caster.top) <= limit));
}


/** The caster's token on this map: the learned id, else the exact name, else
 * a unique first-name match — the same order the bar writer uses. */
export function tokenForCharacter(layout: Layout, c: Character, tokens: MapToken[]): MapToken | null {
  const learned = ((layout.roll20Tokens || {}) as Record<string, { tokenId?: string; name?: string }>)[c.id];
  const aliases = (layout.whisperNames || {}) as Record<string, string>;
  if (learned?.tokenId) { const t = tokens.find((x) => x.id === learned.tokenId); if (t) return t; }
  const want = norm(aliases[c.id] || learned?.name || c.name);
  const exact = tokens.filter((t) => norm(t.name) === want);
  if (exact.length) return exact[0];
  const first = want.split(" ")[0];
  const loose = tokens.filter((t) => first.length >= 3 && norm(t.name).split(" ")[0] === first);
  return loose.length === 1 ? loose[0] : null;
}

export interface AuraOccupant { token: MapToken; character: Character | null }
export interface AuraReach { characterId: string; characterName: string; auraKey: string; auraName: string; radius: number; inside: AuraOccupant[]; casterFound: boolean }

/** For every running aura on the roster: who is inside it right now. */
export function auraReach(layout: Layout, roster: Character[], map: MapRead): AuraReach[] {
  const out: AuraReach[] = [];
  // Tokens on the token layer and the GM layer (a hidden monster still starts
  // its turn inside a Spirit Guardians); map-layer art is scenery.
  const visible = map.tokens.filter((t) => t.layer === "objects" || t.layer === "gmlayer");
  for (const c of roster) for (const a of c.auras || []) {
    const caster = tokenForCharacter(layout, c, visible);
    if (!caster) { out.push({ characterId: c.id, characterName: c.name, auraKey: a.key, auraName: a.name, radius: a.radius, inside: [], casterFound: false }); continue; }
    const inside = tokensInside(caster, a.radius, visible, map.page).map((token) => ({ token, character: characterForToken(layout, roster, token.id, token.name) }));
    out.push({ characterId: c.id, characterName: c.name, auraKey: a.key, auraName: a.name, radius: a.radius, inside, casterFound: true });
  }
  return out;
}

export const occupantName = (o: AuraOccupant) => o.character?.name || o.token.name || "unnamed token";

/* A BAR UPDATE, or nothing.

   On 2026-09-29 a token's Bar 3 was overwritten with the text "NaN", destroying a
   creature's hit points mid-session. Every call site guarded the value it READ from
   the bar and none guarded the number it was about to WRITE, so one piece of bad
   arithmetic reached Roll20 as four characters of nonsense.

   The guard belongs here, at the boundary, not at four call sites that each have to
   remember. Refusing costs one unapplied hit, which the DM can see and redo;
   writing costs a creature's hit points, which nobody can recover.

   Integers only: a bar is a counter, and "12.5" helps nobody. */
/** `max` for the bars that carry one — temporary hit points show N/N. Refused on
 *  the same terms as the value: a non-finite max is no max. */
export function barUpdate(token: { id?: string; name: string }, n: BarNo, value: number, max?: number): Record<string, unknown> | null {
  if (!Number.isFinite(value)) return null;
  const bar: Record<string, string> = { value: String(Math.round(value)) };
  if (max !== undefined && Number.isFinite(max)) bar.max = String(Math.round(max));
  return { tokenId: token.id, name: token.name, [`bar${n}`]: bar };
}